import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons/faCircleCheck';
import { faCircleX } from '@fortawesome/pro-regular-svg-icons/faCircleX';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CreditTransferResponse } from 'us-web-services';

import '../i18n/config';
import { Button, ButtonType } from '../Button';

interface Props {
  partner: string,
  partnerLogo: string,
  creditBalance: number;
  successfulTransfer: boolean;
  setShowDetails(value: boolean): void;
  transferCredits(partner: string, amount: number): Promise<CreditTransferResponse>;
  trackingCallback(event: string, params?: Record<string, unknown>): void;
}

const partnerAmounts: Record<string, number[]> = {
  ActivityHero: [25, 100, 500],
  Outschool: [15, 30, 45, 60],
}

const customAmountValue = -1;

export const StepTwoDetails = ({
  partner,
  partnerLogo,
  creditBalance,
  successfulTransfer,
  setShowDetails,
  transferCredits,
  trackingCallback,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [transferAmount, setTransferAmount] = useState<number>(0);
  const [transferOptions, setTransferOptions] = useState<number[]>([]);
  const [stepTwoInitialValue, setStepTwoInitialValue] = useState<boolean>(true);
  const [creditsTransferred, setCreditsTransferred] = useState<boolean>(successfulTransfer);
  const [isCustomAmount, setIsCustomAmount] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  /*
		TODO: Add unit tests for this
    $100 balance => [$15, $30, $45, $60]
    $50 balance => [$15, $30, $45]
    $5 balance => [$5]
    $0 balance => []
  */
  useEffect(() => {
    const amounts: number[] = [];
    if (creditBalance < partnerAmounts[partner][0] && creditBalance > 0) {
      amounts.push(creditBalance);
    }
    partnerAmounts[partner].forEach(amount => {
      if (amount <= creditBalance) amounts.push(amount);
    });
    setTransferOptions(amounts);
  }, [creditBalance]);

  const handleCancelClicked = (): void => {
    setShowDetails(false);
    const trackingProps = {
      elementText: t('components.partnerWithCredits.cancel'),
    };

    trackingCallback('clicked-button', trackingProps);
  };

  const handleTransferClicked = (): void => {
    transferCredits(partner, transferAmount).then((response) => {
      setCreditsTransferred(response.success);
      if (response.success || (response.error?.code && ['user_not_found', 'user_not_verified'].indexOf(response.error?.code) > -1)) {
        setStepTwoInitialValue(false);
      }
      setErrorMessage(response.error?.message || '');
    });

    const trackingProps = {
      elementText: t('components.partnerWithCredits.transfer'),
      value: transferAmount
    };

    trackingCallback('clicked-button', trackingProps);
  };

  const handleLinkClicked = (): void => {
    const trackingProps = {
      elementText: `Go to ${partner}`,
    };

    trackingCallback('clicked-button', trackingProps);
  };

  const handleTransferOptionClicked = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const trackingProps = {
      elementId: 'partner-transfer',
      type: 'radio',
      value: +e.target.value
    };

    trackingCallback('clicked-button', trackingProps);
    setIsCustomAmount(+e.target.value === customAmountValue);
    if (+e.target.value !== customAmountValue) setTransferAmount(+e.target.value);
  };

  return (
    <>
      {creditsTransferred && (
        <a href='#back' onClick={handleCancelClicked} className='text-sapphire'>
          {'< '}
          {t('components.partnerWithCredits.back')}
        </a>
      )}
      <h3 className='font-semibold text-base'>{t('components.partnerWithCredits.transferHeading', { partner })}</h3>
      <p className='text-xs mb-4'>{t(`components.partnerWithCredits.${partner.toLowerCase()}.transferDetails`)}</p>
      {!creditsTransferred && stepTwoInitialValue && (
        <>
          <fieldset className='mb-8'>
            <legend className='text-sm font-semibold mb-2'>
              {t('components.partnerWithCredits.chooseTheAmount', { partner })}
            </legend>

            {transferOptions?.map(amount => (
              <label key={`amount-${amount}`} className='inline-block mr-4 cursor-pointer'>
                <input
                  type='radio'
                  name='partner-transfer'
                  className='cursor-pointer'
                  value={amount}
                  onChange={handleTransferOptionClicked}
                />
                <span className='text-sm ml-2 align-middle'>${amount}</span>
              </label>
            ))}
            { partner === 'ActivityHero' && (
              <label className='inline-block mr-4 cursor-pointer'>
                <input
                  type='radio'
                  name='partner-transfer'
                  className='cursor-pointer'
                  value={-1}
                  onChange={handleTransferOptionClicked}
                />
                <input
                  type='number'
                  name='custom-amount'
                  min={1}
                  max={Math.min(creditBalance, 1000)}
                  className='cursor-pointer w-28 h-6 ml-1 pl-2 text-sm rounded disabled:bg-gray-200'
                  placeholder='Other'
                  onChange={(e): void => setTransferAmount(+e.target.value)}
                  disabled={!isCustomAmount}
                />
              </label>
            )}
          </fieldset>
          {errorMessage && (
            <div className='text-red-500 -mt-4 mb-8'><FontAwesomeIcon icon={faCircleX} /> {errorMessage}</div>
          )}
          <div>
            <Button
              buttonType={ButtonType.primary}
              onClick={handleTransferClicked}
              disabled={transferAmount <= 0 || transferAmount > Math.min(creditBalance, 1000) || creditsTransferred}
              className='mb-2 mr-3'
            >
              {t('components.partnerWithCredits.transfer')}
            </Button>
            <Button buttonType={ButtonType.secondary} onClick={handleCancelClicked}>
              {t('components.partnerWithCredits.cancel')}
            </Button>
          </div>
        </>
      )}
      {!creditsTransferred && !stepTwoInitialValue && (
        <>
        {errorMessage && (
          <div className='text-red-500 -mt-4 mb-8'><FontAwesomeIcon icon={faCircleX} /> {errorMessage}</div>
        )}
        <a
            href={t(`components.partnerWithCredits.${partner.toLowerCase()}.baseUrl`) || ''}
            target='_blank'
            className='text-center'
            rel='noreferrer'
            onClick={handleLinkClicked}
          >
            <div className='inline-block bg-emerald-50 mt-3 mx-auto py-2 px-4 rounded-xl'>
              <span className='text-xs uppercase tracking-wider'>{t('components.partnerWithCredits.goTo')}</span>
              <img
                className='inline-block ml-2 max-w-[142px]'
                src={partnerLogo}
                alt={t('components.partnerWithCredits.logoAlt', { partner })}
              />
            </div>
          </a>
        </>
      )}
      {creditsTransferred && (
        <>
          <div className='text-green-400'>
            <FontAwesomeIcon icon={faCircleCheck} /> {t('components.partnerWithCredits.transferSuccessful')}
          </div>
          <a
            href={t(`components.partnerWithCredits.${partner.toLowerCase()}.successUrl`) || ''}
            target='_blank'
            className='text-center'
            rel='noreferrer'
            onClick={handleLinkClicked}
          >
            <div className='inline-block bg-emerald-50 mt-3 mx-auto py-2 px-4 rounded-xl'>
              <span className='text-xs uppercase tracking-wider'>{t('components.partnerWithCredits.browse')}</span>
              <img
                className='inline-block ml-2 max-w-[142px]'
                src={partnerLogo}
                alt={t('components.partnerWithCredits.logoAlt', { partner })}
              />
            </div>
          </a>
        </>
      )}
    </>
  );
};
